"use client";

/**
 * Recovers a cold landing on a Clerk verification sub-step.
 *
 * The /login and /signup pages are optional catch-alls
 * ([[...login]] / [[...signup]]), so Clerk owns sub-steps like
 * /login/factor-two or /signup/verify-email-address and keeps the in-progress
 * attempt in client state only. Arriving on one of those URLs cold — a
 * bookmark, a restored browser session, a back/forward nav — leaves nothing for
 * the code/password to attach to, so Clerk renders "No sign-in attempt was
 * found" (or the sign-up equivalent) with no guard and no route back: the only
 * manual recovery is editing the URL down to /login. This guard detects that
 * state and redirects to the base page, carrying redirect_url along so a
 * checkout / invite / funnel flow still lands where it should.
 *
 * Same shape as the first-party cross-link fix in 637e53c1 (PR #1477) — another
 * Clerk sub-route reached in a state the app didn't handle.
 */

import { useEffect, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth, useSignIn, useSignUp } from "@clerk/nextjs";
import { withRedirectUrl } from "@/lib/auth-redirect-url";

export type AuthFlow = "sign-in" | "sign-up";

export const AUTH_STEP_BASE_PATH: Record<AuthFlow, string> = {
  "sign-in": "/login",
  "sign-up": "/signup",
};

// Named steps that bind a form to an in-progress attempt and dead-end when
// landed on cold. Anything NOT listed renders untouched.
//
// This went deny-list for one round and came back. Both shapes need the SAME
// knowledge — which Clerk routes may legitimately arrive with no attempt — so
// neither is "more complete". The only real difference is what happens to a
// route nobody listed, and those outcomes are not symmetric:
//
//   allow-list  → unknown route RENDERS   → a new dead end stays unfixed
//                                            (today's bug, not a regression)
//   deny-list   → unknown route REDIRECTS → a working sign-in strategy breaks
//
// The second is worse: it takes a path that works today and makes it unusable.
// Clerk has attempt-less routes beyond the OAuth/email returns — `choose-wallet`
// (the Web3 callback navigates straight to it) and `enterprise-connections`
// among them — so a deny-list silently breaks those the moment anyone enables
// the strategy. Erring toward rendering can only ever leave a bug unfixed.
//
// The list is evidence-based, not guessed. Disabling the guard and re-running
// `e2e/local/auth-cold-factor-recovery.spec.ts` on the pinned clerk-js —
// NEXT_PUBLIC_CLERK_JS_VERSION (5.127.1) — shows reset-password and
// verify-email-address genuinely strand, while factor-one/factor-two already
// self-heal — see the table in that spec. The lockstep test in
// __tests__/components/auth/auth-step-guard.test.ts fails when the do/*.yaml
// pin moves without this citation (= a re-audit of the sub-route set) moving
// with it.
// The self-healing pair stays listed: redirecting them is harmless (they end up
// at the same place) and it holds if a future release stops self-healing.
//
// Adding a step here needs one check: can Clerk EVER reach it without an
// attempt? If yes it does not belong, whatever else it does.
const STRANDED_STEP_SEGMENTS: Record<AuthFlow, ReadonlySet<string>> = {
  "sign-in": new Set([
    "factor-one",
    "factor-two",
    "reset-password",
    "choose-strategy",
  ]),
  "sign-up": new Set([
    "verify-email-address",
    "verify-phone-number",
    "continue",
  ]),
};

// If clerk-js never loads (blocked, CDN failure, ad-blocker) the redirect can't
// fire, so after this long we surface a plain route out instead of a spinner.
const CLERK_LOAD_TIMEOUT_MS = 5000;

/** True when `pathname` is one of the named sub-steps that requires a prior
 *  in-progress attempt (e.g. /login/factor-two). Unlisted sub-steps return
 *  false and render untouched — see STRANDED_STEP_SEGMENTS for why the default
 *  errs that way. */
export function isVerificationSubStep(flow: AuthFlow, pathname: string): boolean {
  const base = AUTH_STEP_BASE_PATH[flow];
  if (!pathname.startsWith(`${base}/`)) return false;
  // Segment(s) after the base: "/login/factor-two" -> "factor-two"; "/login/"
  // (the base page with a trailing slash) -> "" (not a sub-step).
  const rest = pathname.slice(base.length + 1).replace(/\/+$/, "");
  if (rest === "") return false;
  const segment = rest.split("/")[0] ?? "";
  return STRANDED_STEP_SEGMENTS[flow].has(segment);
}

/** Recovery target for a stranded sub-step: the base page, carrying redirect_url
 *  when a safe internal target is known so the post-login intent survives. */
export function buildStepRecoveryUrl(
  flow: AuthFlow,
  redirectTarget: string | null,
): string {
  return withRedirectUrl(AUTH_STEP_BASE_PATH[flow], redirectTarget);
}

/**
 * A sign-up attempt Clerk has expired (>24h) presents `status: "abandoned"` —
 * non-null, but nothing a verification form can attach to. Treat it exactly
 * like a cold landing: it must not latch the live-attempt suppressor, and it
 * makes a sub-step stranded (#1774). Sign-in never presents it (FAPI prunes
 * abandoned sign-ins to null), so sharing the predicate is harmless there.
 */
export function isLiveAttemptStatus(status: string | null): boolean {
  return status !== null && status !== "abandoned";
}

/**
 * Zero-cost outer shell (#1774): on the BASE auth pages — where `onSubStep`
 * is always false and the guard can never act — render children directly
 * without mounting the Clerk context subscriptions, state, or effects the
 * active guard needs. Only a verification sub-step pays for the machinery.
 */
export function AuthStepGuard({
  flow,
  redirectTarget,
  children,
}: {
  flow: AuthFlow;
  redirectTarget: string | null;
  children: ReactNode;
}) {
  const pathname = usePathname();
  if (!isVerificationSubStep(flow, pathname)) return <>{children}</>;
  return (
    <AuthStepGuardActive flow={flow} redirectTarget={redirectTarget}>
      {children}
    </AuthStepGuardActive>
  );
}

function AuthStepGuardActive({
  flow,
  redirectTarget,
  children,
}: {
  flow: AuthFlow;
  redirectTarget: string | null;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();
  // Both hooks are cheap and always available under ClerkProvider; only the one
  // matching `flow` is read (hooks can't be called conditionally).
  const signInState = useSignIn();
  const signUpState = useSignUp();
  const { isSignedIn } = useAuth();
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  // Latched once a live attempt has been seen on the current sub-step visit —
  // see the warm-completion note on `stranded` below.
  const [sawLiveAttempt, setSawLiveAttempt] = useState(false);
  // The signed-in state the current sub-step visit STARTED with (null until the
  // first loaded render) — distinguishes a pre-existing session, which must
  // still recover, from one minted by a completing attempt, which must not.
  const [wasSignedInAtEntry, setWasSignedInAtEntry] = useState<boolean | null>(
    null,
  );

  const onSubStep = isVerificationSubStep(flow, pathname);
  const { isLoaded, status } =
    flow === "sign-in"
      ? {
          isLoaded: signInState.isLoaded,
          status: signInState.signIn?.status ?? null,
        }
      : {
          isLoaded: signUpState.isLoaded,
          status: signUpState.signUp?.status ?? null,
        };

  // Guarded render-phase adjustments (the React-documented "adjusting state
  // when props change" pattern — cheaper and lint-preferred over an effect):
  // latch on when a live attempt shows on a sub-step, and record the signed-in
  // state the visit STARTED with. Release needs no branch here any more: the
  // zero-cost outer shell unmounts this component the moment the pathname
  // leaves the sub-steps, so a later cold return mounts fresh state. Moving
  // BETWEEN sub-steps (factor-one → factor-two) keeps it mounted, exactly as
  // the latches require.
  if (onSubStep && isLoaded && isLiveAttemptStatus(status) && !sawLiveAttempt) {
    setSawLiveAttempt(true);
  }
  if (onSubStep && isLoaded && wasSignedInAtEntry === null) {
    setWasSignedInAtEntry(isSignedIn === true);
  }

  // A session that APPEARED during this visit — as opposed to one the visitor
  // arrived with. Only the former is evidence of a completing attempt.
  const sessionAppearedThisVisit =
    wasSignedInAtEntry === false && isSignedIn === true;

  // A null status on a verification sub-step means there is no attempt to verify
  // against — the cold-landing dead end. `"abandoned"` (a sign-up attempt Clerk
  // expired after >24h) is the same dead end wearing a non-null status. A live
  // attempt (needs_first_factor, missing_requirements, …) renders the Clerk
  // widget normally.
  //
  // Warm-completion guard: a SUCCESSFUL verification also passes through
  // status === null while still on the sub-step URL — clerk-js's setActive
  // touches the session (a POST whose piggybacked client replaces the attempt
  // with a status-null resource) BEFORE it navigates away, and Clerk's own
  // components suppress exactly this window via their internal
  // setActiveInProgress flag, which useSignIn/useSignUp don't expose. So a
  // completion must never read as stranded: suppress recovery when this visit
  // showed a live attempt OR the session appeared mid-visit (either signal
  // alone identifies the handoff; both together cover either emit ordering).
  //
  // A PRE-EXISTING session must NOT suppress recovery: a signed-in user can
  // reach an attempt-less sub-step cold too (browser history, multi-session
  // "add account") and hits the same dead end — Clerk's <SignIn> stays mounted
  // for them, it does not bounce signed-in users away (Codex, PR #1783).
  // `sawLiveAttempt` exists ONLY for the warm-completion window, and a
  // completing attempt passes through status === null — never "abandoned".
  // So the latch must not suppress the abandoned case: a tab left open on a
  // live verification and revisited after Clerk ages the attempt (>24h) has
  // the latch set from the earlier live render, yet it is exactly as stranded
  // as a cold landing (Codex P2, round 2 on #1819).
  const stranded =
    onSubStep &&
    isLoaded &&
    !sessionAppearedThisVisit &&
    (status === "abandoned" || (status === null && !sawLiveAttempt));

  useEffect(() => {
    if (!stranded) return;
    router.replace(buildStepRecoveryUrl(flow, redirectTarget));
  }, [stranded, flow, redirectTarget, router]);

  // Arm the load-timeout escape hatch only while we're waiting for clerk-js on a
  // sub-step. If it never loads, the redirect above can't fire — so we must not
  // leave the visitor on a bare spinner (a new dead end of the kind we remove).
  useEffect(() => {
    if (!onSubStep || isLoaded) return;
    const timer = setTimeout(() => setClerkTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [onSubStep, isLoaded]);

  // Hold the Clerk widget back on a sub-step until we know an attempt exists, so
  // the dead-end error never flashes before the redirect fires. On the base page
  // (the normal flow) children render immediately.
  if (onSubStep && (stranded || !isLoaded)) {
    if (!isLoaded && clerkTimedOut) {
      return (
        <p className="text-sm text-muted" role="status">
          Still loading…{" "}
          {/* Deliberately a full-document <a>, not next/link: this branch only
              renders when clerk-js failed to load, and a soft SPA nav would keep
              the failed Clerk instance (the script is requested once per
              document) — only a full navigation retries it, so only a full
              navigation is actually a route out. */}
          <a
            href={buildStepRecoveryUrl(flow, redirectTarget)}
            className="font-medium underline hover:text-foreground"
          >
            Return to {flow === "sign-in" ? "sign in" : "sign up"}
          </a>
        </p>
      );
    }
    return (
      <p className="text-sm text-muted" role="status">
        One moment…
      </p>
    );
  }

  return <>{children}</>;
}
